import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import { cs, de, es, fr, pt } from 'date-fns/locale';
import { t, currentLanguage } from '../../i18n/i18n';
import SegmentedTabs from '../SegmentedTabs/SegmentedTabs';
import FilterDropdown from '../FilterDropdown/FilterDropdown';
import { signedUsd } from '../../utilities/format';
import SymbolFlag from '../SymbolFlag/SymbolFlag';
import './DailyPerformance.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });
const KEY = (d) => format(d, 'yyyy-MM-dd');

/**
 * How long a trade was open, in seconds — `open_time` to `close_time`, both on
 * the broker's clock, so the zone cancels out. Null when either end is
 * missing or the two don't make a span.
 */
const heldSeconds = (row) => {
  const open = Date.parse(row?.open_time ?? '');
  const close = Date.parse(row?.close_time ?? '');
  if (!Number.isFinite(open) || !Number.isFinite(close) || close < open) return null;
  return Math.round((close - open) / 1000);
};

/**
 * A span exactly as long as it was, every unit that isn't zero: "42s",
 * "2m 15s", "1h 17m 4s", "2d 3h 5m". A scalp under a minute reads in seconds
 * rather than rounding away to "0m".
 */
const durationText = (seconds) => {
  const parts = [
    [Math.floor(seconds / 86400), 'd'],
    [Math.floor((seconds % 86400) / 3600), 'h'],
    [Math.floor((seconds % 3600) / 60), 'm'],
    [seconds % 60, 's'],
  ]
    .filter(([n]) => n > 0)
    .map(([n, unit]) => `${n}${unit}`);
  return parts.length ? parts.join(' ') : '0s';
};

/** Lots to two places, "0.74", as the reference lists them. */
const lotsText = (volume) => {
  const n = Number(volume);
  return Number.isFinite(n) ? n.toFixed(2) : volume;
};

/**
 * How the Day view's trades can be ordered (review: "a filter option may be
 * helpful … highest profit to lowest"). Newest first is the order they arrive
 * in, so it is the default.
 */
const TRADE_SORTS = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
  { label: 'Highest profit', value: 'profit-desc' },
  { label: 'Lowest profit', value: 'profit-asc' },
];
const closeAt = (row) => Date.parse(row?.close_time ?? '') || 0;
const pnlOf = (row) => Number(row?.net_profit) || 0;
const TRADE_ORDER = {
  newest: (a, b) => closeAt(b) - closeAt(a),
  oldest: (a, b) => closeAt(a) - closeAt(b),
  'profit-desc': (a, b) => pnlOf(b) - pnlOf(a),
  'profit-asc': (a, b) => pnlOf(a) - pnlOf(b),
};

/** Weeks start Monday in the design (Mon…Sun header row — 1:9425). */
const WEEK_OPTS = { weekStartsOn: 1 };

/** Day and month names in the reader's language; English is date-fns' own. */
const DATE_LOCALES = { cs, de, es, fr, pt };

/**
 * Daily performance — 1:9397.
 *
 * Month view is a date-fns calendar heatmap; Day view drills into one date and
 * lists its trades. Stepping moves by month or by day depending on the view.
 *
 *   days    `day_wise_stats` as sent — [{ date, pnl, no_of_trades, … }]
 *   trades  `recent_trades` as sent — [{ symbol, side, volume, open_time, close_time, net_profit, … }]
 *   fill    true when the card sits beside something taller (Account Metrics'
 *           Advanced ledger): the card takes that height instead of adding its
 *           own, and the Day view's list runs the full height of it. Never
 *           shorter than the calendar needs.
 */
export default function DailyPerformance({
  days = [],
  trades = [],
  initialMonth = new Date(),
  initialSelected = null,
  onSelectDay,
  view: viewProp,
  onViewChange,
  fill = false,
}) {
  const [month, setMonth] = useState(() => startOfMonth(initialMonth));
  const [selected, setSelected] = useState(initialSelected);
  const [viewState, setViewState] = useState('Month');

  const locale = DATE_LOCALES[currentLanguage()];
  const view = viewProp ?? viewState;
  const setView = onViewChange ?? setViewState;
  const isDay = view === 'Day';

  /**
   * The Day view is held to the calendar's height. A busy day lists more
   * trades than the month grid is tall, and the card growing with them
   * stretched the row — the Statistics card beside it on Simple pulled tall
   * with its figures spread apart. So the calendar's body is measured each
   * time it shows, the Day view takes exactly that height, and its trade list
   * scrolls inside it.
   */
  const monthBodyRef = useRef(null);
  const [monthBodyHeight, setMonthBodyHeight] = useState(null);
  /**
   * The whole card's height in Month view — head, gap, calendar, padding. In
   * `fill` the card's own content no longer sizes it, so this is its floor:
   * the calendar is never cut short by a shorter neighbour.
   */
  const [monthCardHeight, setMonthCardHeight] = useState(null);
  useLayoutEffect(() => {
    const body = monthBodyRef.current;
    if (isDay || !body) return;
    setMonthBodyHeight(body.offsetHeight);
    const card = body.parentElement;
    const head = card?.firstElementChild;
    if (card && head) {
      const cs = getComputedStyle(card);
      setMonthCardHeight(
        head.offsetHeight +
          body.offsetHeight +
          (parseFloat(cs.rowGap) || 0) +
          (parseFloat(cs.paddingTop) || 0) +
          (parseFloat(cs.paddingBottom) || 0),
      );
    }
    // `fill` too: Simple and Advanced give the card different widths, so a
    // mode switch re-measures the calendar rather than keep the other's size.
  }, [isDay, month, fill]);
  // In `fill` the Day view takes the card's height, not the calendar's.
  const dayHeight = fill ? null : monthBodyHeight;
  const cursor = selected ?? month;

  const grid = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month), WEEK_OPTS),
        end: endOfWeek(endOfMonth(month), WEEK_OPTS),
      }),
    [month]
  );

  const weekdays = useMemo(() => grid.slice(0, 7).map((d) => format(d, 'EEE', { locale })), [grid, locale]);

  // Strongest positive day this month, used to pick the deeper blue.
  const peak = useMemo(() => {
    const inMonth = days.filter((row) => row.date.startsWith(format(month, 'yyyy-MM')));
    return Math.max(0, ...inMonth.map((row) => row.pnl));
  }, [month, days]);

  const toneFor = (d) => {
    if (!isSameMonth(d, month)) return 'outside';
    const entry = days.find((row) => row.date.startsWith(KEY(d)));
    if (!entry) return 'neutral';
    if (entry.pnl < 0) return 'loss';
    if (entry.pnl > 0) return peak && entry.pnl >= peak * 0.6 ? 'profit-strong' : 'profit';
    return 'neutral';
  };

  const choose = (d) => {
    setSelected(d);
    onSelectDay?.(d, days.find((row) => row.date.startsWith(KEY(d))));
  };

  /**
   * A click on the grid is a request to read that day, so it drills in rather
   * than only tinting the footer — the footer keeps the selection for when the
   * Month tab is picked again.
   */
  const openDay = (d) => {
    choose(d);
    setView('Day');
  };

  // Stepping in Day view moves the selection and follows it into a new month.
  const step = (delta) => {
    const next = delta < 0 ? subDays(cursor, 1) : addDays(cursor, 1);
    setMonth(startOfMonth(next));
    choose(next);
  };

  const active = selected && days.find((row) => row.date.startsWith(KEY(selected)));
  const [tradeSort, setTradeSort] = useState('newest');
  const dayTrades = selected
    ? trades.filter((row) => row.close_time?.startsWith(KEY(selected)))
    : [];
  /**
   * The day's time in market — every trade's hold added up (review: "Cannot
   * view any duration stats"). Only when every trade has both ends, so a
   * partial sum is never passed off as the whole.
   */
  const dayHeld = dayTrades.map(heldSeconds);
  const dayInMarket =
    dayHeld.length && dayHeld.every((m) => m != null)
      ? durationText(dayHeld.reduce((sum, m) => sum + m, 0))
      : null;

  return (
    <section
      className={`daily${fill ? ' daily--fill' : ''}`}
      style={fill && monthCardHeight ? { minHeight: monthCardHeight } : undefined}
    >
      <header className="daily__head">
        <span className="daily__title">
          <span className="icon daily__title-icon" style={icon('metrics-calendar')} />
          {t('Daily performance')}
        </span>

        <div className="daily__controls">
          <SegmentedTabs radius="track" options={['Month', 'Day']} value={view} onChange={setView} />
          {/* Ordering for the Day view's trade list — only there, where there
              is a list to order. Sits with the day stepper on the right. */}
          {isDay && (
            <FilterDropdown
              className="daily__sort"
              options={TRADE_SORTS}
              value={tradeSort}
              onChange={setTradeSort}
              label="Sort"
            />
          )}
          <div className="daily__nav">
            <button
              type="button"
              className="daily__step"
              aria-label={isDay ? t('Previous day') : t('Previous month')}
              onClick={() => (isDay ? step(-1) : setMonth((m) => subMonths(m, 1)))}
            >
              <span className="icon daily__step-icon is-prev" style={icon('chevron-down-md')} />
            </button>
            <span className="daily__month">
              {isDay ? format(cursor, 'd MMM yyyy', { locale }) : format(month, 'MMM yyyy', { locale })}
            </span>
            <button
              type="button"
              className="daily__step"
              aria-label={isDay ? t('Next day') : t('Next month')}
              onClick={() => (isDay ? step(1) : setMonth((m) => addMonths(m, 1)))}
            >
              <span className="icon daily__step-icon is-next" style={icon('chevron-down-md')} />
            </button>
          </div>
        </div>
      </header>

      {isDay ? (
        <div
          className={`daily__body daily__body--day${fill || dayHeight ? '' : ' is-unmeasured'}`}
          style={dayHeight ? { height: dayHeight } : undefined}
        >
          <div className="daily__day-head">
            <div className="daily__foot-left">
              <span className="daily__day-date">{format(cursor, 'EEEE, d MMMM', { locale })}</span>
              <span className="daily__foot-meta">
                {active ? t('{{count}} trades', { count: active.no_of_trades }) : t('No trades')}
                {active && dayInMarket ? `, ${t('{{duration}} in market', { duration: dayInMarket })}` : ''}
              </span>
            </div>
            <span className={`daily__day-pnl is-${(active?.pnl ?? 0) < 0 ? 'down' : 'up'}`}>
              {signedUsd(active?.pnl ?? 0, { decimals: 2 })}
            </span>
          </div>

          <ul className="daily__trades">
            {[...dayTrades].sort(TRADE_ORDER[tradeSort]).map((row, i) => (
              <li className="daily__trade" key={`${row.position_id}-${i}`}>
                <SymbolFlag symbol={row.symbol.split('.')[0]} />
                <div className="daily__trade-id">
                  <span className="daily__trade-symbol">{row.symbol}</span>
                  <span className="daily__trade-meta">
                    {/* "Sell · 0.74 lots · 12:48 · 10m 32s" — when it opened
                        (broker clock) and exactly how long it was open; the
                        close time alone when no open time came back. */}
                    {t(row.side)} · {lotsText(row.volume)} {t('lots')} ·{' '}
                    {heldSeconds(row) != null
                      ? `${row.open_time.slice(11, 16)} · ${durationText(heldSeconds(row))}`
                      : row.close_time.slice(11, 16)}
                  </span>
                </div>
                <div className="daily__trade-figures">
                  <span className={`daily__trade-pnl is-${row.net_profit < 0 ? 'down' : 'up'}`}>
                    {signedUsd(row.net_profit, { decimals: 2 })}
                  </span>
                  {row.exit_reason && <span className="daily__trade-result">{t(row.exit_reason)}</span>}
                </div>
              </li>
            ))}
            {!dayTrades.length && <li className="daily__empty">{t('No trades on this day')}</li>}
          </ul>
        </div>
      ) : (
        <div className="daily__body" ref={monthBodyRef}>
          <div className="daily__weekdays">
            {weekdays.map((w) => (
              <span key={w} className="daily__weekday">
                {w}
              </span>
            ))}
          </div>

          <div className="daily__grid">
            {grid.map((d) => {
              const tone = toneFor(d);
              const isSelected = selected && isSameDay(d, selected);
              // Outside days are greyed filler, so today only marks inside the month.
              const today = tone !== 'outside' && isToday(d);
              const entry = tone !== 'outside' && days.find((row) => row.date.startsWith(KEY(d)));
              // The day's P&L exactly as `day_wise_stats` sends it, to the cent —
              // the same figure the footer shows, never rounded for the cell.
              const amount = entry ? signedUsd(entry.pnl, { decimals: 2, fallback: null }) : null;
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={tone === 'outside'}
                  aria-current={isSelected ? 'date' : undefined}
                  aria-label={`${format(d, 'EEEE, d MMMM yyyy', { locale })}${today ? `, ${t('today')}` : ''}${
                    entry ? `, ${signedUsd(entry.pnl, { decimals: 2 })}` : ''
                  }`}
                  className={`daily__day is-${tone}${isSelected ? ' is-selected' : ''}${today ? ' is-today' : ''}`}
                  onClick={() => openDay(d)}
                >
                  <span className="daily__day-num">{format(d, 'd')}</span>
                  {amount && <span className="daily__day-amount">{amount}</span>}
                </button>
              );
            })}
          </div>

          <div className="daily__rule" />

          <footer className="daily__foot">
            <div className="daily__foot-left">
              <span className="daily__foot-date">
                {selected ? format(selected, 'EEEE, d MMMM', { locale }) : t('No day selected')}
              </span>
              <span className="daily__foot-meta">
                {active ? t('{{count}} trades', { count: active.no_of_trades }) : t('Pick a day to see its detail')}
              </span>
            </div>
            {active && (
              <span className={`daily__foot-pnl is-${active.pnl < 0 ? 'down' : 'up'}`}>
                {signedUsd(active.pnl, { decimals: 2 })}
              </span>
            )}
          </footer>
        </div>
      )}
    </section>
  );
}
